import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth/AuthProvider";

export type CheckinStatus = "scheduled" | "completed" | "missed" | "escalated" | "canceled";

export interface SafetyCheckin {
  id: string;
  user_id: string;
  scheduled_at: string;
  grace_minutes: number;
  context: string | null;
  status: CheckinStatus;
  completed_at: string | null;
  escalated_at: string | null;
  created_at: string;
}

export function useSafetyCheckins() {
  const { user } = useAuth();
  const [checkins, setCheckins] = useState<SafetyCheckin[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) {
      setCheckins([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data } = await (supabase as any)
      .from("safety_checkins")
      .select("*")
      .eq("user_id", user.id)
      .order("scheduled_at", { ascending: false })
      .limit(50);
    setCheckins((data as SafetyCheckin[]) ?? []);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    void load();
  }, [load]);

  const schedule = useCallback(
    async (scheduledAt: Date, graceMinutes: number, context?: string) => {
      if (!user) return { error: "not_signed_in" };
      const { error } = await (supabase as any).from("safety_checkins").insert({
        user_id: user.id,
        scheduled_at: scheduledAt.toISOString(),
        grace_minutes: graceMinutes,
        context: context ?? null,
        status: "scheduled" as CheckinStatus,
      });
      if (error) return { error: error.message };
      await load();
      return { error: null };
    },
    [user, load],
  );

  const complete = useCallback(
    async (id: string) => {
      const { error } = await supabase.rpc("fn_complete_checkin", { _id: id });
      if (error) return { error: error.message };
      await load();
      return { error: null };
    },
    [load],
  );

  const cancel = useCallback(
    async (id: string) => {
      const { error } = await (supabase as any)
        .from("safety_checkins")
        .update({ status: "canceled" as CheckinStatus })
        .eq("id", id);
      if (error) return { error: error.message };
      await load();
      return { error: null };
    },
    [load],
  );

  const upcoming = checkins.filter((c) => c.status === "scheduled");
  const past = checkins.filter((c) => c.status !== "scheduled");

  return { checkins, upcoming, past, loading, schedule, complete, cancel, reload: load };
}
